import { Pressable, Text, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { Chip } from '../index';
import type { DropsetDraft, DropsetVariant } from '@jackedlogs/shared';
import type { ConfirmFn } from '../seams';
import type { useTheme } from './theme';
import { makeStyles } from './loggerStyles';

// Slice 4.4 — when the user activates "Set type" from ⋯ (or the set is
// already a dropset/myorep), reps/weight/rpe/× hide and the variant
// chips take their place inline. The drop steps below carry the
// per-step reps/weight.
//
// Slice 5 — picking a variant is delegated to the parent (onPickVariant):
// activating on a fresh set seeds the chain from the working set via
// proposeDropsetScheme and clears the parent inputs; swapping the variant
// on an existing chain just relabels it. Removing the chain (×) hands the
// top round back to the parent row (onRemoveChain).
export function SetTypeChipBar({
  dropset,
  inputsLocked,
  onPickVariant,
  onRemoveChain,
  onCloseTypeBar,
  helpMode,
  showTip,
  confirm,
  theme,
}: {
  dropset: DropsetDraft | null;
  inputsLocked: boolean;
  onPickVariant: (variant: DropsetVariant) => void;
  onRemoveChain: () => void;
  onCloseTypeBar: () => void;
  /** RW-25 — the host's confirm seam. The × below discards logged drop steps,
   *  and react-native's Alert never fires on react-native-web. */
  confirm: ConfirmFn;
  /** Help mode (Burn+ S2): a chip tap explains the technique instead of
   *  selecting it. Owned by the FocusPanel's useHelpMode. */
  helpMode: boolean;
  /** Shared tip surface — pinned in help mode. */
  showTip: (message: string, opts?: { pinned?: boolean }) => void;
  theme: ReturnType<typeof useTheme>;
}) {
  const { t } = useTranslation();
  const styles = makeStyles(theme);
  const helpHint = helpMode
    ? t('mobile.logger.helpModeHint', {
        defaultValue: 'Help mode is on — this explains instead of changing the setting.',
      })
    : undefined;
  return (
    <>
      <Chip
        theme={theme}
        dense
        hitSlop={{ top: 8, bottom: 8 }}
        label={t('mobile.logger.setTypeDropset', { defaultValue: 'Dropset' })}
        selected={dropset?.variant === 'classical'}
        accessibilityHint={helpHint}
        onPress={() =>
          helpMode
            ? showTip(t('workout.dropsetHintClassical'), { pinned: true })
            : onPickVariant('classical')
        }
        disabled={inputsLocked}
      />
      <Chip
        theme={theme}
        dense
        hitSlop={{ top: 8, bottom: 8 }}
        label={t('mobile.logger.setTypeMyorep', { defaultValue: 'Myo-rep' })}
        selected={dropset?.variant === 'myorep'}
        accessibilityHint={helpHint}
        onPress={() =>
          helpMode
            ? showTip(t('workout.dropsetHintMyorep'), { pinned: true })
            : onPickVariant('myorep')
        }
        disabled={inputsLocked}
      />
      {/* The old `?` Alert that explained the two techniques was retired
          2026-07-02 — the shared help mode (? toggle in the qualifier row)
          now explains Dropset/Myo-rep the same way as every other chip. */}
      <View style={styles.dropsetControlSpacer} />
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={t('workout.dropsetRemove')}
        onPress={() => {
          if (dropset === null) {
            onCloseTypeBar();
            return;
          }
          const hasData = dropset.steps.some(
            (st) =>
              st.reps.trim() !== '' ||
              st.weight.trim() !== '' ||
              st.completedAt !== null,
          );
          if (!hasData) {
            onRemoveChain();
            onCloseTypeBar();
            return;
          }
          void confirm({
            title: t('workout.dropsetRemove'),
            body: t('workout.dropsetRemoveConfirm', {
              defaultValue:
                'You have {{n}} drop steps. Convert back to a working set and discard them?',
              n: dropset.steps.length,
            }),
            confirmLabel: t('workout.dropsetRemove'),
            cancelLabel: t('workout.cancel'),
          }).then((ok) => {
            if (!ok) return;
            onRemoveChain();
            onCloseTypeBar();
          });
        }}
        disabled={inputsLocked}
        hitSlop={8}
        style={({ pressed }) => [
          styles.dropsetRemoveChip,
          pressed && styles.iconButtonPressed,
        ]}
      >
        <Text style={styles.dropsetRemoveChipText}>×</Text>
      </Pressable>
    </>
  );
}
